/**
 * useCurrentArtifact
 *
 * Single resolver for the form/entity nesting contract that the old provider
 * tree (`FormProvider` / `FormVersionProvider` / `EntityProvider` /
 * `EntityRecordProvider`) supplied implicitly. Returns:
 *
 * - `kind`: which artifact the leaf binds to. Entity scope wins over form
 *   scope when both are present (innermost-wins). `'none'` when no scope.
 * - `data`: the active document (`EntityRecord` for entity, `FormVersion`
 *   for form), or `null` when not yet loaded.
 * - `formId` / `versionId` when `kind === 'form'`, `entityId` / `recordId`
 *   when `kind === 'entity'`.
 * - `insideFormContext` / `insideEntityContext`: independent context flags.
 *   Both can be `true` simultaneously (entity scope mounted inside a form
 *   route). Permission-aware consumers read both; data-binding consumers
 *   narrow on `kind`.
 *
 * Consumers: `useAttachments`, `AttachmentsUI`, `UnmappedFieldsUI`,
 * `CommunicationLogUI`, `FormEmailModal`, `WorkflowParameterSidebar`. Direct
 * `useFormStore()` / `useEntityRecordScope()` calls are reserved for form-only
 * or entity-only state and writers, after `kind` narrowing.
 *
 * ⚠️ `data` on the form branch is a GETTER, not a property — do not "simplify"
 * it (SYD-2252). This hook reads `formStore.state` through `useSnapshot`, so
 * Valtio's proxy-compare refines each consumer's subscription down to the
 * deepest path it actually touches. An eager `data: snap.formVersion.data`
 * would register a terminal access to `data` for EVERY consumer — including
 * the ids-only ones — leaving proxy-compare nothing deeper to refine on, so it
 * falls back to ref-comparing `data`, which changes on every keystroke. Behind
 * a getter, only consumers that really read `data` track it, and they track
 * just the subtree they read.
 *
 * The form branch passes the slot document through untouched, so `data.docId`
 * is the id `mergeDocumentIntoProxy` injected — read `versionId` instead when
 * you need the id the route selected. `data` is a frozen snapshot: never
 * mutate it in place.
 */

import { useSnapshot } from 'valtio';
import type { EntityRecord, FormVersion } from '#types/System.types';
import { formStore, useEntityRecordScope } from '../store';

type ArtifactBase = {
    insideFormContext: boolean;
    insideEntityContext: boolean;
};

export type CurrentArtifact = ArtifactBase & (
    | {
          kind: 'form';
          formId: string;
          versionId: string | undefined;
          data: FormVersion | null;
          loading: boolean;
      }
    | {
          kind: 'entity';
          entityId: string;
          recordId: string | undefined;
          data: EntityRecord | null;
          loading: boolean;
      }
    | { kind: 'none' }
);

export const useCurrentArtifact = (): CurrentArtifact => {
    const scope = useEntityRecordScope();
    const snap = useSnapshot(formStore.state);

    const insideFormContext = !!snap.formId;
    const insideEntityContext = scope.insideEntityRecordContext;

    if (insideEntityContext) {
        return {
            kind: 'entity',
            entityId: scope.entityId,
            recordId: scope.recordId,
            data: scope.entityRecord.data,
            loading: scope.entityRecord.loading,
            insideFormContext,
            insideEntityContext,
        };
    }

    if (insideFormContext) {
        return {
            kind: 'form',
            formId: snap.formId!,
            versionId: snap.versionId,
            get data() { return (snap.formVersion.data ?? null) as FormVersion | null; },
            loading: snap.formVersion.loading,
            insideFormContext,
            insideEntityContext,
        };
    }

    return { kind: 'none', insideFormContext, insideEntityContext };
};
